import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { FALLBACK_LOCALE, TAbsoluteLocale, TCountry } from '@src/config/i18n.config'
import { noop } from '@src/utils/functions/utils'
import { setLocalisationDataInStorage } from '@src/utils/general.utils'
import { TRegionDetail } from '@src/utils/i18n.utils'

import useLocaleContextHandlers from './hooks/useLocaleContextHandlers'
import withI18nGate from './with-i18n-gate.hoc'

export const LocaleContext = createContext<{
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  isLocaleModalOpen: boolean
  setIsLocaleModalOpen: (isLocaleModalOpen: boolean) => void
  setLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  isLocaleConfirmationModalOpen: boolean
  showConfirmationModal: (country: TCountry, callback: () => void) => void
  closeConfirmationModal: (confirm: boolean) => void
  confirmationCountry: TCountry | null
}>({
  locale: FALLBACK_LOCALE,
  regionDetails: { regions: {}, regionCodes: [] },
  isLocaleModalOpen: false,
  setIsLocaleModalOpen: noop,
  setLocale: noop,
  isLocaleConfirmationModalOpen: false,
  showConfirmationModal: noop,
  closeConfirmationModal: noop,
  confirmationCountry: null,
})

export const LocaleContextProvider = withI18nGate(
  ({
    children,
    initialLocale,
    regionDetails,
  }: {
    initialLocale: TAbsoluteLocale
    children: React.ReactNode
    regionDetails: TRegionDetail
  }) => {
    const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false)
    const [confirmationCountry, setConfirmationCountry] = useState<TCountry | null>(null)
    const [isLocaleConfirmationModalOpen, setIsLocaleConfirmationModalOpen] = useState(false)
    const [locale, setLocale] = useState(initialLocale)

    useEffect(() => {
      setLocalisationDataInStorage(initialLocale, regionDetails)
    }, [initialLocale, regionDetails])

    const { handleSetLocale, closeConfirmationModal, showConfirmationModal } = useLocaleContextHandlers({
      setIsLocaleConfirmationModalOpen,
      setConfirmationCountry,
      regionDetails,
      setLocale,
    })

    const providerValue = useMemo(() => {
      return {
        locale,
        isLocaleModalOpen,
        setIsLocaleModalOpen,
        setLocale: handleSetLocale,
        regionDetails,
        isLocaleConfirmationModalOpen,
        confirmationCountry,
        showConfirmationModal,
        closeConfirmationModal,
      }
    }, [
      locale,
      isLocaleModalOpen,
      regionDetails,
      isLocaleConfirmationModalOpen,
      confirmationCountry,
      handleSetLocale,
      showConfirmationModal,
      closeConfirmationModal,
    ])

    return <LocaleContext.Provider value={providerValue}>{children}</LocaleContext.Provider>
  },
  true,
)

export const useLocaleContext = () => {
  return useContext(LocaleContext)
}
